import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Copy, Check, ThumbsUp, ThumbsDown, Share2, FileText, FileType } from 'lucide-react';
import { copyOryaTextToClipboard } from '../../relatorios/utils/relatoriosOryaClipboard';
import { submitAprenderOryaFeedback } from '../documentationOryaService';
import {
  APRENDER_ORYA_LIKE_REASONS,
  APRENDER_ORYA_DISLIKE_REASONS,
} from '../documentationOryaFeedbackOptions';

function ActionTooltip({ text, align = 'center' }) {
  return (
    <span className={`aprender-orya-action-tooltip aprender-orya-action-tooltip--${align}`} role="tooltip">
      {text}
    </span>
  );
}

function FeedbackModal({ type, onClose, onSubmit, submitting }) {
  const reasons = type === 'like' ? APRENDER_ORYA_LIKE_REASONS : APRENDER_ORYA_DISLIKE_REASONS;
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');

  const handleSubmit = () => {
    if (!reasonCode || submitting) return;
    onSubmit({ reason_code: reasonCode, reason_note: note.trim() || null });
  };

  return (
    <div className="aprender-orya-feedback-modal" role="dialog" aria-modal="true">
      <p className="aprender-orya-feedback-modal-title">
        {type === 'like' ? 'Do que você gostou?' : 'O que deu errado?'}
      </p>
      <div className="aprender-orya-feedback-options">
        {reasons.map((item) => (
          <div key={item.code} className="aprender-orya-feedback-option-wrap">
            <label className="aprender-orya-feedback-option">
              <input
                type="radio"
                name="orya-feedback-reason"
                value={item.code}
                checked={reasonCode === item.code}
                onChange={() => setReasonCode(item.code)}
              />
              <span className="aprender-orya-feedback-option-text">
                <strong>{item.title}</strong>
                {item.description ? <span>{item.description}</span> : null}
              </span>
            </label>
            {reasonCode === item.code ? (
              <textarea
                className="aprender-orya-feedback-note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="(Opcional) Conte mais sobre sua experiência"
              />
            ) : null}
          </div>
        ))}
      </div>
      <div className="aprender-orya-feedback-modal-actions">
        <button type="button" className="aprender-orya-feedback-cancel" onClick={onClose}>
          Cancelar
        </button>
        <button
          type="button"
          className="aprender-orya-feedback-submit"
          disabled={!reasonCode || submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'Enviando...' : 'Enviar opinião'}
        </button>
      </div>
    </div>
  );
}

export default function DocumentationOryaMessageActions({
  messageId,
  content,
  questionText,
  sources,
  feedback,
  onFeedback,
  exportTitle = 'orya-aprender-resposta',
}) {
  const [copied, setCopied] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [popoverPos, setPopoverPos] = useState(null);
  const [shareMenuPos, setShareMenuPos] = useState(null);
  const rowRef = useRef(null);
  const shareWrapRef = useRef(null);
  const shareMenuRef = useRef(null);
  const feedbackPopoverRef = useRef(null);
  const POPOVER_WIDTH = 392;
  const SHARE_MENU_WIDTH = 168;
  const VIEWPORT_PAD = 8;
  const POPOVER_GAP = 10;

  const computeAnchoredPopoverPosition = useCallback((anchorRect, popoverEl, width) => {
    if (!anchorRect) return null;
    const popH = popoverEl?.offsetHeight ?? 320;
    let left = anchorRect.right - width;
    left = Math.max(VIEWPORT_PAD, Math.min(left, window.innerWidth - width - VIEWPORT_PAD));

    const spaceBelow = window.innerHeight - anchorRect.bottom - VIEWPORT_PAD;
    const spaceAbove = anchorRect.top - VIEWPORT_PAD;
    let top =
      spaceBelow >= popH + POPOVER_GAP || spaceBelow >= spaceAbove
        ? anchorRect.bottom + POPOVER_GAP
        : anchorRect.top - popH - POPOVER_GAP;
    top = Math.max(VIEWPORT_PAD, Math.min(top, window.innerHeight - popH - VIEWPORT_PAD));

    return { left, width, top };
  }, []);

  useEffect(() => {
    if (!shareOpen) return undefined;
    const onDoc = (e) => {
      if (
        shareWrapRef.current?.contains(e.target) ||
        shareMenuRef.current?.contains(e.target)
      ) {
        return;
      }
      setShareOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [shareOpen]);

  useLayoutEffect(() => {
    if (!shareOpen || !shareWrapRef.current) {
      setShareMenuPos(null);
      return undefined;
    }
    const update = () => {
      const rect = shareWrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const next = computeAnchoredPopoverPosition(rect, shareMenuRef.current, SHARE_MENU_WIDTH);
      if (next) setShareMenuPos(next);
    };
    update();
    const raf = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [shareOpen, computeAnchoredPopoverPosition]);

  useLayoutEffect(() => {
    if (!modalType || !rowRef.current) {
      setPopoverPos(null);
      return undefined;
    }
    const update = () => {
      const rect = rowRef.current?.getBoundingClientRect();
      if (!rect) return;
      const next = computeAnchoredPopoverPosition(rect, feedbackPopoverRef.current, POPOVER_WIDTH);
      if (next) setPopoverPos(next);
    };
    update();
    const raf = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    const ro =
      typeof ResizeObserver !== 'undefined' && feedbackPopoverRef.current
        ? new ResizeObserver(update)
        : null;
    if (ro && feedbackPopoverRef.current) ro.observe(feedbackPopoverRef.current);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      ro?.disconnect();
    };
  }, [modalType, computeAnchoredPopoverPosition]);

  const handleCopy = useCallback(async () => {
    const ok = await copyOryaTextToClipboard(content);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [content]);

  const handleExport = useCallback(
    async (type) => {
      if (exporting || !String(content || '').trim()) return;
      setExporting(type);
      setShareOpen(false);
      try {
        if (type === 'pdf') {
          const { exportOryaAprenderAnswerToPdf } = await import('../../relatorios/utils/relatoriosOryaAnswerExport');
          await exportOryaAprenderAnswerToPdf(content, exportTitle);
        } else {
          const { exportOryaAnswerToWord } = await import('../../relatorios/utils/relatoriosOryaAnswerExport');
          await exportOryaAnswerToWord(content, exportTitle);
        }
      } catch (_) {
        /* silencioso */
      } finally {
        setExporting(null);
      }
    },
    [content, exportTitle, exporting]
  );

  const handleSubmitFeedback = useCallback(
    async (payload) => {
      const rating = modalType;
      if (!rating) return;
      setSubmitting(true);
      try {
        await submitAprenderOryaFeedback({
          rating,
          reason_code: payload.reason_code,
          reason_note: payload.reason_note,
          message_local_id: messageId,
          question_text: questionText,
          answer_text: content,
          sources,
        });
        onFeedback?.({
          rating,
          reason_code: payload.reason_code,
          reason_note: payload.reason_note,
        });
        setModalType(null);
      } catch (_) {
        /* mantém modal aberto */
      } finally {
        setSubmitting(false);
      }
    },
    [modalType, messageId, questionText, content, sources, onFeedback]
  );

  const rated = feedback?.rating;

  return (
    <div className="aprender-orya-message-actions">
      {modalType
        ? createPortal(
            <>
              <button
                type="button"
                className="aprender-orya-feedback-backdrop"
                aria-label="Fechar formulário"
                onClick={() => setModalType(null)}
              />
              <div
                ref={feedbackPopoverRef}
                className="aprender-orya-feedback-popover"
                style={{
                  visibility: popoverPos ? 'visible' : 'hidden',
                  left: popoverPos?.left ?? VIEWPORT_PAD,
                  width: popoverPos?.width ?? POPOVER_WIDTH,
                  top: popoverPos?.top ?? VIEWPORT_PAD,
                }}
              >
                <FeedbackModal
                  type={modalType}
                  onClose={() => setModalType(null)}
                  onSubmit={handleSubmitFeedback}
                  submitting={submitting}
                />
              </div>
            </>,
            document.body
          )
        : null}
      {shareOpen
        ? createPortal(
            <div
              ref={shareMenuRef}
              className="aprender-orya-share-popover"
              style={{
                visibility: shareMenuPos ? 'visible' : 'hidden',
                left: shareMenuPos?.left ?? VIEWPORT_PAD,
                width: shareMenuPos?.width ?? SHARE_MENU_WIDTH,
                top: shareMenuPos?.top ?? VIEWPORT_PAD,
              }}
            >
              <div className="aprender-orya-share-menu" role="menu">
                <button
                  type="button"
                  className="aprender-orya-share-item"
                  role="menuitem"
                  onClick={() => handleExport('word')}
                  disabled={exporting === 'word'}
                >
                  <FileText size={14} strokeWidth={2} aria-hidden />
                  <span>Baixar Word</span>
                </button>
                <button
                  type="button"
                  className="aprender-orya-share-item"
                  role="menuitem"
                  onClick={() => handleExport('pdf')}
                  disabled={exporting === 'pdf'}
                >
                  <FileType size={14} strokeWidth={2} aria-hidden />
                  <span>Baixar PDF</span>
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
      <div ref={rowRef} className="aprender-orya-message-actions-row">
        <button
          type="button"
          className="aprender-orya-action-btn"
          onClick={handleCopy}
          aria-label="Copiar resposta"
        >
          <ActionTooltip text={copied ? 'Copiado!' : 'Copiar resposta'} align="start" />
          {copied ? <Check size={15} strokeWidth={2} aria-hidden /> : <Copy size={15} strokeWidth={2} aria-hidden />}
        </button>
        <button
          type="button"
          className={`aprender-orya-action-btn${rated === 'like' ? ' aprender-orya-action-btn--active-like' : ''}`}
          onClick={() => !rated && setModalType('like')}
          disabled={Boolean(rated)}
          aria-label="Marcar como útil"
          aria-pressed={rated === 'like'}
        >
          <ActionTooltip text="Resposta útil" />
          <ThumbsUp size={15} strokeWidth={2} aria-hidden />
        </button>
        <button
          type="button"
          className={`aprender-orya-action-btn${rated === 'dislike' ? ' aprender-orya-action-btn--active-dislike' : ''}`}
          onClick={() => !rated && setModalType('dislike')}
          disabled={Boolean(rated)}
          aria-label="Marcar como não útil"
          aria-pressed={rated === 'dislike'}
        >
          <ActionTooltip text="Resposta não útil" />
          <ThumbsDown size={15} strokeWidth={2} aria-hidden />
        </button>
        <div className="aprender-orya-share-wrap" ref={shareWrapRef}>
          <button
            type="button"
            className="aprender-orya-action-btn"
            onClick={() => setShareOpen((v) => !v)}
            aria-label="Compartilhar resposta"
            aria-expanded={shareOpen}
            disabled={Boolean(exporting)}
          >
            <ActionTooltip text="Compartilhar" />
            <Share2 size={15} strokeWidth={2} aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}
